/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, getter-return */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { get, action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import moment from 'moment';
import { cleanFileName } from '@linc-technologies/linced-common/utils/file-names';
import { stripDiacritics } from 'ember-power-select/utils/group-utils';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';

export default class GenerateUploadController extends Controller {
  @action
  setNameType(value) {
    this.nameType = value;
  }

  @action
  setTerm(value) {
    this.term = value;
  }

  queryParams = ['type'];

  @service
  session;

  @service
  csv;

  @tracked type = 'nzcer';
  year = moment().year();
  @tracked
  validated = false;
  @tracked
  invalid = [];
  @tracked
  data = [];
  @tracked term = 1;
  @tracked
  nameType = 'preferred';

  @alias('session.school.moeYearLevels')
  yearLevels;

  @tracked
  selectedYearLevels = [];
  @tracked
  selectedClasses = [];

  get typeUpper() {
    if (this.type === 'nzcer') return 'NZCER';
    if (this.type === 'smart') return 'SMART';
    return 'e-asTTle';
  }

  @computed('selectedClasses.[]', 'selectedYearLevels.[]')
  get buttonDisabled() {
    return (
      !this.selectedClasses.length ||
      (this.type === 'nzcer' && !this.selectedYearLevels.length)
    );
  }

  @computed('session.school', 'term', 'type')
  get fileName() {
    let jurisdictionalId = this.session.school.jurisdictionalId;
    let year = this.year.toString().substr(2);

    if (this.type === 'nzcer') {
      let rawFileName = `NZCER${jurisdictionalId}-${year}-${this.term}`;
      return cleanFileName(rawFileName) + '.csv';
    }

    if (this.type === 'e-asttle') {
      let rawFileName = `${jurisdictionalId}_e-asTTle-4`;
      return cleanFileName(rawFileName) + '.ast';
    }

    if (this.type === 'smart') {
      let rawFileName = `${jurisdictionalId}_smart`;
      return cleanFileName(rawFileName) + '.ast';
    }
  }

  @action
  toggleClass(field) {
    this.toggleFieldSelected(field, 'selectedClasses');
  }

  @action
  toggleYearLevel(field) {
    this.toggleFieldSelected(field, 'selectedYearLevels');
  }

  @action
  generateData() {
    let data = this.getExportData();
    let invalid = this.validateData(data);

    this.data = data;
    this.invalid = invalid;
    this.validated = true;
  }

  @action
  downloadCsv() {
    let rows = this.generateCsv(this.data);
    return this.csv.export(rows, {
      fileName: this.fileName,
      withSeparator: false,
      raw: true,
    });
  }

  @action
  selectAllClasses() {
    this.selectedClasses = this.model.homerooms.slice();
  }

  @action
  selectAllYears() {
    this.selectedYearLevels = this.yearLevels.slice();
  }

  @action
  selectNone(name) {
    this.set(name, []);
  }

  @action
  startOver() {
    this.reset();
  }

  reset() {
    this.selectedYearLevels = [];
    this.selectedClasses = [];
    this.data = [];
    this.invalid = [];
    this.validated = false;
    this.term = 1;
    this.nameType = 'preferred';
  }

  getExportData() {
    let students = [];
    let studentClasses = [];

    this.selectedClasses.forEach((homeroom) => {
      homeroom.currentLearners.forEach((learner) => {
        let person = learner.person;
        if (
          this.type === 'nzcer' &&
          !this.selectedYearLevels.includes(person.currentYearLevel.toString())
        ) {
          return;
        }

        if (this.type === 'smart' && person.get('currentYearLevel') < 3) {
          return;
        }

        let data = this.extractStudentData(person);
        students.push(data);
        studentClasses.push({
          studentId: data.studentId,
          nsn: data.nsn,
          homeroomId: data.homeroomId,
        });
      });
    });

    return { students, studentClasses };
  }

  // cleanString performs any string "cleaning" required by the specific type of import.
  cleanString(input) {
    if (this.type === 'nzcer') {
      return stripDiacritics(input);
    }
    return input;
  }

  extractStudentData(student) {
    let studentId = student.heroId;
    let nsn = student.get('fields.nzMOE:nsn.value');
    let surname = this.cleanString(student.lastName);
    let firstName = this.cleanString(student.firstName);
    let legalLastName = this.cleanString(student.legalLastName);
    let legalFirstName = this.cleanString(student.legalFirstName);
    let currentYearLevel = student.currentYearLevel;
    let homeroomId = get(student, 'homeroom.heroId');
    let gender = student.gender;
    let language = student.returnLanguageCode; // Spoken at home. 1 = English, 2 = Language other than English, blank = unknown.
    let nzcerLanguage = isEmpty(language) ? '' : language - 1; // Spoken at home. 0 = English, 1 = Language other than English, blank = unknown.
    let ethnicities = student.get('fields.nzMOE:ethnicity.value') || [];
    let ethnicOrigin1 = ethnicities[0] || '';
    let ethnicOrigin2 = ethnicities[1] || '';
    let ethnicOrigin3 = ethnicities[2] || '';

    return {
      studentId,
      nsn,
      surname,
      firstName,
      legalLastName,
      legalFirstName,
      currentYearLevel,
      homeroomId,
      gender,
      language,
      nzcerLanguage,
      ethnicOrigin1,
      ethnicOrigin2,
      ethnicOrigin3,
    };
  }

  validateData({ students, studentClasses }) {
    let invalid = [];

    students.forEach((row) => {
      let errors = {
        'Student ID': [],
        NSN: [],
        Surname: [],
        'First Name': [],
        'Current Year Level': [],
        Gender: [],
        Ethnicity: [],
        'Student Classes': [],
        student: row,
      };

      // Student ID
      if (isEmpty(row.studentId)) {
        errors['Student ID'].push('Field must not be blank.');
      }

      // NSN
      if (isEmpty(row.nsn)) {
        errors['NSN'].push('Field must not be blank.');
      }

      // This is already validated on the backend, but including for completeness.
      if (!/^\d+$/.test(row.nsn)) {
        errors['NSN'].push('NSN must only contain digits.');
      }

      if (!isEmpty(row.nsn) && row.nsn.length !== 10) {
        errors['NSN'].push('NSN must be 10 digits in length');
      }

      // Surname
      if (isEmpty(row.surname)) {
        errors['Surname'].push('Field must not be blank.');
      }

      // First Name
      if (isEmpty(row.firstName)) {
        errors['First Name'].push('Field must not be blank.');
      }

      // Current Year Level
      if (isEmpty(row.currentYearLevel)) {
        errors['Current Year Level'].push('Field must not be blank.');
      }

      if (this.type === 'e-asttle' || this.type === 'smart') {
        if (
          !isEmpty(row.currentYearLevel) &&
          (row.currentYearLevel < 1 || row.currentYearLevel > 13)
        ) {
          errors['Current Year Level'].push('Field must be between 1 and 13.');
        }
      }

      if (this.type === 'nzcer') {
        if (
          !isEmpty(row.currentYearLevel) &&
          (row.currentYearLevel < 0 || row.currentYearLevel > 15)
        ) {
          errors['Current Year Level'].push('Field must be between 0 and 15.');
        }
      }

      // Gender
      if (isEmpty(row.gender)) {
        errors['Gender'].push('Field must not be blank.');
      }

      if (!isEmpty(row.gender) && ['M', 'F'].indexOf(row.gender) === -1) {
        errors['Gender'].push('Gender must be either M or F.');
      }

      // Ethnicity
      if (isEmpty(row.ethnicOrigin1)) {
        errors['Ethnicity'].push('Field must not be blank.');
      }

      const DMU_CODES = [
        '111',
        '121',
        '122',
        '123',
        '124',
        '125',
        '126',
        '127',
        '128',
        '129',
        '211',
        '311',
        '321',
        '331',
        '341',
        '351',
        '361',
        '371',
        '411',
        '412',
        '413',
        '414',
        '421',
        '431',
        '441',
        '442',
        '443',
        '444',
        '511',
        '521',
        '531',
        '611',
        '999',
      ];

      if (
        !isEmpty(row.ethnicOrigin1) &&
        DMU_CODES.indexOf(row.ethnicOrigin1) === -1
      ) {
        errors['Ethnicity'].push('Ethnicity code is invalid.');
      }

      // Student/Classes
      if (isEmpty(findBy(studentClasses, 'studentId', row.studentId))) {
        errors['Student Classes'].push(
          'Student does not belong to any classes.',
        );
      }

      // If there are any errors, add to error report.
      if (
        Object.values(errors).reduce(
          (hasError, column) => hasError || column.length,
          false,
        )
      ) {
        invalid.push(errors);
      }
    });

    return invalid;
  }

  generateCsv({ students, studentClasses }) {
    let rows = [];
    let type = this.type;

    let jurisdictionalId = this.session.school.jurisdictionalId;
    let fileCreated = moment().format('YYYY-MM-DD HH:mm:ss');
    if (type === 'e-asttle' || type === 'smart') {
      fileCreated += '.0';
    }

    // Section: Header
    rows.push(
      ['SECTION', 'Header'],
      [this.fileName, fileCreated, type === 'nzcer' ? this.term : 10],
    );

    // Section: Import_Type
    if (type === 'e-asttle' || type === 'smart') {
      rows.push(['SECTION', 'Import_Type'], ['Class']);
    } else {
      // NZCER has empty rows between sections
      rows.push([]);
    }

    // Section: School
    rows.push(
      ['SECTION', 'School', 'ROWS', 1],
      type === 'nzcer'
        ? [jurisdictionalId, this.cleanString(this.session.school.name)]
        : [jurisdictionalId],
    );

    if (type === 'nzcer') {
      // NZCER has empty rows between sections
      rows.push([]);
    }

    // Section: Class
    rows.push(['SECTION', 'Class', 'ROWS', this.selectedClasses.length]);

    this.selectedClasses
      .slice()
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .forEach((homeroom) => {
        rows.push([homeroom.heroId, this.cleanString(homeroom.name), 'Y']);
      });

    if (type === 'nzcer') {
      // NZCER has empty rows between sections
      rows.push([]);
    }

    // Section: Student
    let excludeIds = this.invalid.map((i) => i.student.studentId);

    let studentRows = students
      .filter((student) => excludeIds.indexOf(student.studentId) === -1)
      .map((student) => {
        if (type === 'e-asttle' || type === 'smart') {
          return [
            student.studentId,
            student.nsn,
            student.legalLastName,
            student.legalFirstName,
            student.currentYearLevel,
            student.gender,
            student.language,
            student.ethnicOrigin1,
          ];
        }

        if (type === 'nzcer') {
          return [
            student.studentId,
            student.nsn,
            this.nameType === 'preferred'
              ? student.surname
              : student.legalLastName,
            this.nameType === 'preferred'
              ? student.firstName
              : student.legalFirstName,
            student.currentYearLevel,
            student.currentYearLevel,
            student.currentYearLevel,
            student.gender,
            student.nzcerLanguage,
            student.ethnicOrigin1,
            student.ethnicOrigin2,
            student.ethnicOrigin3,
          ];
        }
      });

    rows.push(['SECTION', 'Student', 'ROWS', studentRows.length]);

    rows.push(...studentRows);

    if (type === 'nzcer') {
      // NZCER has empty rows between sections
      rows.push([]);
    }

    // Section: Student_Class
    let studentClassRows = studentClasses
      .filter(
        (studentClass) => excludeIds.indexOf(studentClass.studentId) === -1,
      )
      .map((studentClass) => {
        return [
          studentClass.studentId,
          studentClass.nsn,
          studentClass.homeroomId,
        ];
      });

    rows.push(['SECTION', 'Student_Class', 'ROWS', studentClassRows.length]);

    rows.push(...studentClassRows);

    if (type === 'nzcer') {
      // NZCER has empty rows between sections
      rows.push([]);
    }

    // Section: Footer
    rows.push(['SECTION', 'Footer'], [this.fileName]);

    return rows;
  }

  toggleFieldSelected(field, propName) {
    let selected = this[propName];
    if (selected.includes(field)) {
      this[propName] = selected.filter((item) => item !== field);
    } else {
      this[propName] = [...selected, field];
    }
  }
}
